/**
 * 認証済み画面の外枠。`@mail-sv/ui` の `<AppShell>` にナビとトップバーを差し込むだけで、
 * レイアウトの実装はここに持たない（admin / portal でシェルがずれないようにするため）。
 */
import * as React from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, FlaskConical, LogOut, ShieldAlert, TriangleAlert, UserRound } from 'lucide-react';
import {
  AppShell,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  formatDateTime,
  type NavItem,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { endImpersonation, fetchImpersonations, logout } from '../lib/auth.js';
import { buildNavGroups } from '../lib/nav.js';
import { qk } from '../lib/query-keys.js';
import { authStore, useAuth } from '../stores/auth.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import type { DashboardSummary, NotificationUnreadCount } from '../types/api.js';
import { navTo } from './nav-link.js';

/** dev ビルドかどうか。本番ビルドでは `undefined` になり、この定数ごと消える。 */
const IS_DEV_BUILD = import.meta.env.VITE_DEV_LOGIN === '1';
const BRAND_NAME = import.meta.env.VITE_BRAND_NAME ?? 'Aurora Host';

export function AppLayout(): React.ReactElement {
  const { user, impersonating } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // ナビのバッジ（失敗ジョブ / 応答なしノード）はダッシュボードの集計に相乗りする。
  // 専用エンドポイントを増やすより、既に 15 秒ごとに取っているものを使い回す方が安い。
  const summary = useQuery({
    queryKey: qk.dashboard(),
    queryFn: () => api.get<DashboardSummary>('/dashboard'),
    refetchInterval: 15_000,
    enabled: (user?.permissions.includes(OPS_PERMISSION.nodeRead) ?? false),
  });

  const badges = React.useMemo(() => {
    const result: Record<string, { value: number; tone?: 'default' | 'warning' | 'destructive' }> = {};
    const d = summary.data;
    if (!d) return result;
    result.nodes = { value: d.nodes.unreachable, tone: 'destructive' };
    result.jobs = { value: d.alerts.find((a) => a.kind === 'job_failed')?.count ?? 0, tone: 'warning' };
    return result;
  }, [summary.data]);

  const navGroups = React.useMemo(
    () => buildNavGroups(user?.permissions, badges),
    [user?.permissions, badges],
  );

  /*
   * **自分が張っている代理セッションをサーバから復元する。**
   * 赤帯をクライアント状態だけで描いていたため、リロードすると消えて
   * 「代理中であることを運営者が見失う」状態になっていた（サーバ側の
   * セッションは最大 60 分生きたまま顧客データが開いている）。
   * 参照は `ops.security.read` なので、権限が無ければ引かない。
   */
  const canSeeImpersonations = user?.permissions.includes(OPS_PERMISSION.securityRead) ?? false;
  const mine = useQuery({
    queryKey: qk.impersonations({ scope: 'mine' }),
    queryFn: () => fetchImpersonations(),
    enabled: canSeeImpersonations,
    refetchInterval: 60_000,
  });

  React.useEffect(() => {
    if (!mine.data || !user) return;
    const active = mine.data.find((s) => s.active && s.staffUserId === user.id);
    authStore.setImpersonating(
      active
        ? {
            impersonationSessionId: active.id,
            organizationId: active.organizationId,
            organizationName: active.organizationName ?? active.organizationId,
            scope: active.scope,
            expiresAt: active.expiresAt,
          }
        : null,
    );
  }, [mine.data, user]);

  return (
    <AppShell
      brand={{ name: BRAND_NAME, sub: '運営コンソール' }}
      navGroups={navGroups}
      currentPath={pathname}
      renderLink={renderNavLink}
      topBarRight={<TopBarRight />}
      footer={user ? <UserFooter email={user.email} role={user.role} /> : null}
      storageKey="mail-sv.admin.sidebar.collapsed"
    >
      {impersonating ? <ImpersonationBanner session={impersonating} /> : null}
      {/*
        ops 面は TOTP 必須（`OPS_MFA_REQUIRED`）。未登録のまま通常画面を出すと、
        どのページを開いても API が `MFA_REQUIRED` を返し「全部壊れている」ように見える。
        初回ログインの運営者に「まずこれをやる」を 1 つだけ見せる。
        設定画面だけは通す（そこに登録フローがあるため）。
      */}
      {shouldShowMfaGate(user, pathname) ? <MfaEnrollmentGate /> : <Outlet />}
    </AppShell>
  );
}

/**
 * ガードを出すかどうかの判定。**関数として切り出してあるのは検査するため**
 * （`app-layout.test.tsx`）。3 つの条件はどれも外すと実害が出る:
 *
 *   - `user` が無い間（`/auth/ops/me` の応答待ち）は**出さない**。
 *     未ログイン相当の状態でガードを出すと、ログイン画面へ戻る動きと
 *     重なって「ログインし直してもガードに戻る」ように見える。
 *   - `mfaEnabled` が true なら通常画面。
 *   - `/settings` 配下だけは通す。**そこに登録フローがある**ので、
 *     ここを閉じるとガードのボタンを押しても何も起きない
 *     （＝運営者が永久に 2FA を登録できない）。
 */
export function shouldShowMfaGate(
  user: { mfaEnabled: boolean } | null | undefined,
  pathname: string,
): boolean {
  if (!user) return false;
  if (user.mfaEnabled) return false;
  return !pathname.startsWith('/settings');
}

/**
 * 2 要素認証が未登録の運営者に出す全画面ガード。
 * ここで止めるのは意地悪ではなく、ops 面の API が実際に全部 403 を返すため。
 */
export function MfaEnrollmentGate(): React.ReactElement {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-16 text-center">
      <div className="rounded-full bg-destructive/15 p-4">
        <ShieldAlert className="size-8 text-destructive" aria-hidden />
      </div>
      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-foreground">2 要素認証を登録してください</h1>
        <p className="text-sm text-muted-foreground">
          運営コンソールは、顧客のメールデータに触れられる管理画面です。
          そのため 2 要素認証（TOTP）の登録が完了するまで、各機能は利用できません。
          認証アプリ（Google Authenticator / 1Password など）を用意して、下のボタンから登録してください。
        </p>
      </div>
      <Button asChild>
        <Link to="/settings" search={{ tab: 'account' }}>
          2 要素認証を登録する
        </Link>
      </Button>
      <p className="text-xs text-muted-foreground">
        登録すると復旧コードが表示されます。認証アプリを失くしたときに必要なので、必ず控えてください。
      </p>
    </div>
  );
}

function renderNavLink(item: NavItem, content: React.ReactNode, className: string): React.ReactNode {
  return (
    <Link to={navTo(item.href)} className={className}>
      {content}
    </Link>
  );
}

function TopBarRight(): React.ReactElement {
  const { user } = useAuth();
  return (
    <div className="flex items-center gap-2">
      <NotificationBell perms={user?.permissions} />
      {IS_DEV_BUILD ? (
        /*
          **狭い画面でも必ず出す。**「本番だと思って触ったら開発環境だった」
          （あるいはその逆）を防ぐためのバッジなので、`hidden sm:inline-flex`
          で消してしまうと、いちばん取り違えやすいスマートフォンでだけ
          警告が無くなる。文字だけを `sm` 未満で畳み、アイコンは残す。
        */
        <Badge variant="warning" title="VITE_DEV_LOGIN=1 のビルドです">
          <FlaskConical aria-hidden />
          <span className="hidden sm:inline">開発環境</span>
          <span className="sr-only sm:hidden">開発環境</span>
        </Badge>
      ) : null}
      {user && !user.mfaEnabled ? (
        <Badge variant="destructive" title="2 要素認証が未登録です">
          <ShieldAlert aria-hidden />
          2FA 未登録
        </Badge>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
            aria-label="アカウントメニュー"
          >
            <UserRound className="size-4" />
            <span className="hidden max-w-[12rem] truncate sm:inline">{user?.displayName ?? '—'}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel>
            <span className="block truncate">{user?.email}</span>
            <span className="block text-xs font-normal text-muted-foreground">
              {labelOf(user?.role)}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings" search={{ tab: 'account' }}>
              アカウント設定
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void logout()}>
            <LogOut className="size-4" />
            ログアウト
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * 運営向け通知の未読バッジ。
 *
 * `audience='staff'` の通知（ノード無応答・バックアップ異常・監査 WORM の
 * 未アンカー・到達性の悪化）は**読む口がどこにも無かった**。critical は
 * メールにも昇格するが、warning / info は事実上どこにも出ていない。
 * 1 分ごとに件数だけ引き、未読があるときだけベルを目立たせる。
 */
function NotificationBell({ perms }: { perms: readonly string[] | undefined }): React.ReactElement | null {
  const enabled = perms?.includes(OPS_PERMISSION.securityRead) ?? false;
  const count = useQuery({
    queryKey: qk.notificationUnread(),
    queryFn: () => api.get<NotificationUnreadCount>('/notifications/unread-count'),
    enabled,
    refetchInterval: 60_000,
  });

  if (!enabled) return null;
  const unread = count.data?.unread ?? 0;
  const critical = count.data?.critical ?? 0;

  return (
    <Link
      to="/support"
      search={{ tab: 'notifications', openOnly: true, unreadOnly: true }}
      className="relative flex items-center rounded-md px-2 py-1.5 text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
      aria-label={unread > 0 ? `未読の通知が ${unread} 件あります` : '運営通知'}
      title={unread > 0 ? `未読 ${unread} 件（うち緊急 ${critical} 件）` : '運営通知'}
    >
      <Bell className="size-4" aria-hidden />
      {unread > 0 ? (
        <Badge
          variant={critical > 0 ? 'destructive' : 'warning'}
          size="sm"
          className="absolute -right-0.5 -top-0.5 min-w-4 justify-center px-1 py-0 text-[10px] leading-4"
        >
          {unread > 99 ? '99+' : unread}
        </Badge>
      ) : null}
    </Link>
  );
}

function UserFooter({ email, role }: { email: string; role: string }): React.ReactElement {
  return (
    <div className="min-w-0 rounded-md bg-panel-elevated px-2.5 py-2">
      <p className="truncate text-xs font-medium text-foreground">{email}</p>
      <p className="text-[11px] text-muted-foreground">{labelOf(role)}</p>
    </div>
  );
}

/**
 * 代理ログイン中である旨の赤帯。
 * 実際の操作は別タブの契約者パネルで行うが、「今どこかで代理セッションが生きている」ことを
 * 運営者の視界から消さないために admin 側にも常時出す。
 *
 * **ボタンは「表示を消す」ではなく実際の終了**（`POST /orgs/:id/impersonate/end`）。
 * 表示だけ消す実装だと、運営者は終わらせたつもりで席を立ち、サーバ側の代理
 * セッションは期限まで生き続ける（＝顧客データが開いたままになる）。
 */
function ImpersonationBanner({
  session,
}: {
  session: NonNullable<ReturnType<typeof useAuth>['impersonating']>;
}): React.ReactElement {
  const qc = useQueryClient();
  const end = useMutation({
    mutationFn: () => endImpersonation(session.organizationId),
    onSuccess: () => {
      authStore.setImpersonating(null);
      void qc.invalidateQueries({ queryKey: qk.impersonations() });
    },
  });

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3">
      <TriangleAlert className="size-4 shrink-0 text-destructive" aria-hidden />
      <p className="min-w-0 flex-1 text-sm text-destructive">
        <strong>{session.organizationName}</strong> として操作中のセッションがあります（
        {session.scope === 'read_write' ? '書き込み可' : '読み取り専用'}・
        {formatDateTime(session.expiresAt)} まで）。操作はすべて監査ログに記録されます。
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => end.mutate()}
        loading={end.isPending}
        className="border-destructive/50"
      >
        代理を終了する
      </Button>
    </div>
  );
}
